import { useState, useEffect } from 'react';

const Analytics = ({ darkMode = false }) => {
  const [analytics, setAnalytics] = useState({
    averageFocusTime: 0,
    averageMood: 0,
    totalSessions: 0,
    completedSessions: 0,
    moodEntries: 0,
    focusStreak: 0,
    moodTrend: 'stable'
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7'); // 7 days by default

  useEffect(() => {
    calculateAnalytics();
  }, [dateRange]);

  const calculateAnalytics = () => {
    setLoading(true);
    
    const days = parseInt(dateRange);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    // Load focus sessions (we'll need to track these in the Pomodoro timer)
    const focusSessions = JSON.parse(localStorage.getItem('focusSessions') || '[]');
    
    // Load mood entries
    const moodHistory = JSON.parse(localStorage.getItem('moodHistory') || '[]');
    
    // Filter data for the selected time period
    const recentSessions = focusSessions.filter(session => 
      new Date(session.timestamp) >= cutoffDate
    );
    
    const recentMoods = moodHistory.filter(mood => 
      new Date(mood.timestamp) >= cutoffDate
    );
    
    // Calculate focus statistics
    const completedSessions = recentSessions.filter(session => session.completed).length;
    const totalSessions = recentSessions.length;
    const totalFocusTime = recentSessions.reduce((sum, session) => 
      sum + (session.duration || 0), 0
    );
    const averageFocusTime = totalSessions > 0 ? totalFocusTime / totalSessions : 0;
    
    // Calculate mood statistics
    const moodEntries = recentMoods.length;
    const totalMoodValue = recentMoods.reduce((sum, mood) => sum + mood.value, 0);
    const averageMood = moodEntries > 0 ? totalMoodValue / moodEntries : 0;
    
    // Calculate focus streak (consecutive days with completed sessions)
    const focusStreak = calculateFocusStreak(focusSessions);
    
    // Calculate mood trend
    const moodTrend = calculateMoodTrend(recentMoods);
    
    setAnalytics({
      averageFocusTime: Math.round(averageFocusTime),
      averageMood: parseFloat(averageMood.toFixed(1)),
      totalSessions,
      completedSessions,
      moodEntries,
      focusStreak,
      moodTrend
    });
    
    setLoading(false);
  };

  const calculateFocusStreak = (sessions) => {
    if (sessions.length === 0) return 0;
    
    const today = new Date();
    let streak = 0;
    let currentDate = new Date(today);
    
    for (let i = 0; i < 30; i++) { // Check last 30 days
      const dateStr = currentDate.toDateString();
      const dayHasSessions = sessions.some(session => 
        new Date(session.timestamp).toDateString() === dateStr && session.completed
      );
      
      if (dayHasSessions) {
        streak++;
      } else if (currentDate.toDateString() !== today.toDateString()) {
        // Don't break streak for today if no sessions yet
        break;
      }
      
      currentDate.setDate(currentDate.getDate() - 1);
    }
    
    return streak;
  };

  const calculateMoodTrend = (moods) => {
    if (moods.length < 2) return 'stable';
    
    const sortedMoods = [...moods].sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );
    
    const firstHalf = sortedMoods.slice(0, Math.floor(sortedMoods.length / 2));
    const secondHalf = sortedMoods.slice(Math.floor(sortedMoods.length / 2));
    
    const firstAvg = firstHalf.reduce((sum, mood) => sum + mood.value, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, mood) => sum + mood.value, 0) / secondHalf.length;
    
    const difference = secondAvg - firstAvg;
    
    if (difference > 0.3) return 'improving';
    if (difference < -0.3) return 'declining';
    return 'stable';
  };

  const formatFocusTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getMoodEmoji = (value) => {
    if (value >= 4.5) return '😄';
    if (value >= 3.5) return '😊';
    if (value >= 2.5) return '😐';
    if (value >= 1.5) return '😔';
    return '😢';
  };

  const getTrendEmoji = (trend) => {
    switch (trend) {
      case 'improving': return '📈';
      case 'declining': return '📉';
      default: return '➡️';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'improving': return '#4CAF50';
      case 'declining': return '#f44336';
      default: return '#667eea';
    }
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="analytics-card">
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Calculating your analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto font-sans">
      <div className="wellness-card p-8 lg:p-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-12">
          <div className="space-y-3">
            <h2 className={`text-2xl lg:text-3xl font-semibold tracking-tight font-sans ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              📊 Your Mental Health Analytics
            </h2>
            <p className={`text-lg font-sans leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Track your mindful progress and discover patterns in your well-being journey
            </p>
          </div>
          <select 
            className={`wellness-input font-sans ${
              darkMode 
                ? 'bg-gray-800/50 border-gray-600 text-white focus:border-purple-400' 
                : 'bg-white/50 border-gray-300 text-gray-800 focus:border-purple-400'
            }`}
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="3">Last 3 days</option>
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
          </select>
        </div>

        <div className="">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Focus Analytics */}
            <div className={`p-8 rounded-3xl shadow-lg transition-all duration-300 hover:scale-105 backdrop-blur-sm border ${
              darkMode ? 'bg-gradient-to-br from-blue-900/20 to-purple-900/20 border-gray-700/30' : 'bg-gradient-to-br from-blue-50/70 to-purple-50/70 border-white/40'
            }`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold font-sans ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  🍅 Focus Sessions
                </h3>
              </div>
              <div className="space-y-6">
                <div className="text-center">
                  <div className={`text-4xl font-bold wellness-text ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {formatFocusTime(analytics.averageFocusTime)}
                  </div>
                  <div className={`text-base mt-2 wellness-text ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Average Focus Time
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="space-y-2">
                    <div className={`text-2xl font-semibold font-sans ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {analytics.completedSessions}
                    </div>
                    <div className={`text-sm font-sans wellness-text ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Completed
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className={`text-2xl font-semibold font-sans ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {analytics.totalSessions}
                    </div>
                    <div className={`text-sm font-sans wellness-text ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Total
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mood Analytics */}
            <div className={`p-8 rounded-3xl shadow-lg wellness-transition hover:scale-105 ${
              darkMode ? 'bg-gradient-to-br from-purple-900/30 to-pink-900/30' : 'bg-gradient-to-br from-purple-50 to-pink-50'
            }`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold font-sans ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  😊 Mood Tracking
                </h3>
              </div>
              <div className="space-y-4">
                <div className="text-center">
                  <div className={`text-3xl font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    {getMoodEmoji(analytics.averageMood)} {analytics.averageMood}/5
                  </div>
                  <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Average Mood
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div>
                    <div className={`text-xl font-semibold font-sans ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {analytics.moodEntries}
                    </div>
                    <div className={`text-xs font-sans ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Entries
                    </div>
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${
                      analytics.moodTrend === 'improving' ? 'text-green-500' :
                      analytics.moodTrend === 'declining' ? 'text-red-500' : 'text-yellow-500'
                    }`}>
                      {getTrendEmoji(analytics.moodTrend)} {analytics.moodTrend}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Streak & Consistency */}
            <div className={`p-6 rounded-xl ${
              darkMode ? 'bg-gradient-to-br from-orange-900/30 to-red-900/30' : 'bg-gradient-to-br from-orange-50 to-red-50'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold font-sans ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  🔥 Consistency
                </h3>
              </div>
              <div className="space-y-4">
                <div className="text-center">
                  <div className={`text-3xl font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                    {analytics.focusStreak}
                  </div>
                  <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Day Focus Streak
                  </div>
                </div>
                <div className="text-center">
                  <div className={`text-xl font-semibold font-sans ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {analytics.totalSessions > 0 
                      ? Math.round((analytics.completedSessions / analytics.totalSessions) * 100)
                      : 0}%
                  </div>
                  <div className={`text-xs font-sans ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Completion Rate
                  </div>
                </div>
              </div>
            </div>

            {/* Insights */}
            <div className={`p-6 rounded-xl ${
              darkMode ? 'bg-gradient-to-br from-green-900/30 to-emerald-900/30' : 'bg-gradient-to-br from-green-50 to-emerald-50'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold font-sans ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  💡 Insights
                </h3>
              </div>
              <div className="space-y-3">
                {analytics.focusStreak >= 7 && (
                  <div className={`p-3 rounded-lg ${
                    darkMode ? 'bg-green-900/40' : 'bg-green-100'
                  }`}>
                    <div className="flex items-start space-x-2">
                      <span className="text-lg">🎉</span>
                      <span className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                        Amazing! You've maintained a {analytics.focusStreak}-day focus streak!
                      </span>
                    </div>
                  </div>
                )}
                {analytics.averageMood >= 4 && (
                  <div className={`p-3 rounded-lg ${
                    darkMode ? 'bg-blue-900/40' : 'bg-blue-100'
                  }`}>
                    <div className="flex items-start space-x-2">
                      <span className="text-lg">😊</span>
                      <span className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                        Your mood has been consistently positive lately!
                      </span>
                    </div>
                  </div>
                )}
                {analytics.moodTrend === 'improving' && (
                  <div className={`p-3 rounded-lg ${
                    darkMode ? 'bg-purple-900/40' : 'bg-purple-100'
                  }`}>
                    <div className="flex items-start space-x-2">
                      <span className="text-lg">📈</span>
                      <span className={`text-sm ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                        Your mood trend is improving - keep it up!
                      </span>
                    </div>
                  </div>
                )}
                {analytics.completedSessions === 0 && analytics.totalSessions > 0 && (
                  <div className={`p-3 rounded-lg ${
                    darkMode ? 'bg-yellow-900/40' : 'bg-yellow-100'
                  }`}>
                    <div className="flex items-start space-x-2">
                      <span className="text-lg">💪</span>
                      <span className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                        Try breaking tasks into smaller chunks to improve completion rate.
                      </span>
                    </div>
                  </div>
                )}
                {analytics.moodEntries === 0 && (
                  <div className={`p-3 rounded-lg ${
                    darkMode ? 'bg-gray-800/40' : 'bg-gray-100'
                  }`}>
                    <div className="flex items-start space-x-2">
                      <span className="text-lg">📝</span>
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Start tracking your mood to see patterns over time!
                      </span>
                    </div>
                  </div>
                )}
                {analytics.averageMood < 2.5 && analytics.moodEntries > 3 && (
                  <div className={`p-3 rounded-lg ${
                    darkMode ? 'bg-red-900/40' : 'bg-red-100'
                  }`}>
                    <div className="flex items-start space-x-2">
                      <span className="text-lg">🤗</span>
                      <span className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                        Consider reaching out to friends or professionals for support.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
